import { useState, useCallback } from 'react'

import { getItem, setItem } from 'shared/utils/storage'
import { window } from 'shared/utils/browser'

type StateCallback<T> = {
  (value: T): void
}
const clientSessionStorage = window?.sessionStorage

const useStorage = <Type>(
  key: string,
  initialValue: Type,
  storage: Storage | undefined = clientSessionStorage,
  valueIsJson = true,
): [Type, StateCallback<Type>] => {
  const [storedValue, setStoredValue] = useState<Type>(() => {
    try {
      const item = getItem(key, storage)
      if (item && valueIsJson) {
        return JSON.parse(item)
      }
      return item || initialValue
    } catch (error) {
      return initialValue
    }
  })

  const setValue = useCallback(
    (value: Type, optionalKey?: string) => {
      if (window) {
        const valueToStore =
          value instanceof Function ? value(storedValue) : value
        setStoredValue(valueToStore)

        const stringValue =
          typeof valueToStore === 'string'
            ? valueToStore
            : JSON.stringify(valueToStore)

        setItem(optionalKey ? optionalKey : key, stringValue, storage)
      }
    },
    [storedValue, storage, key],
  )

  return [storedValue, setValue]
}

export default useStorage
